import React from 'react';
import { eq, join, concat, set, filter } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQuery, useMutation } from '@apollo/client';
import { Section } from '.';
import ChildSection from './childSection';
import { GENERAL_SETTINGS, PAGE_DATA, UPDATE_PAGE_DATA, SET_CLIENT_STATE } from '../../../graphqls';
import { backgroundBlendModes } from '../../page/homepage';

export default function HomepageSettings() {
  const {
    loading: loadingGeneralSettings,
    data: { generalSettings: { showProgress: progressable, navTextColor: defaultNavTextColor } = {} } = {},
  } = useQuery(GENERAL_SETTINGS);
  const {
    loading: loadingPageData,
    data: {
      pageData: {
        cache: {
          columns: numberOfColumns = 3,
          children = [],
          subContent: subTexts = [],
          headerPosition,
          banner: {
            enable: showBanner,
            backgroundColor: bannerBackgroundColor,
            textDecorationColor,
            textDecoration,
            withImage,
            opacity,
            imageBlendMode,
          } = {},
          navTextColor,
          newsFeed: { enabled: showNews, backgroundColor: feedBackgroundColor, position: newsFeedPosition } = {},
          introContent: introTexts = [],
          mainContentBackgroundColor,
          overlapCardTitle = 'bottom',
          showCardDescription = false,
          showProgress,
        } = {},
      } = {},
    } = {},
  } = useQuery(PAGE_DATA);
  const [setState] = useMutation(SET_CLIENT_STATE);
  const [updatePageData] = useMutation(UPDATE_PAGE_DATA);

  if (loadingGeneralSettings || loadingPageData) {
    return '';
  }

  const rows = [];
  const numberOfRows = Math.ceil(children.length / numberOfColumns);
  const childCards = children.map((child, index) => (
    <ChildSection isHomePage updatePageData={updatePageData} key={child.planNodeId} childIndex={index} childData={child} />
  ));
  let firstIndex = 0;

  for (let i = 1; i <= numberOfRows; i += 1) {
    rows.push(
      <div className="child-card-row" key={`${i}child_cards_row`}>
        {childCards.slice(firstIndex, numberOfColumns * i)}
      </div>,
    );
    firstIndex += numberOfColumns;
  }

  const updateHeaderPosition = (position) => {
    updatePageData({ variables: { cache: { headerPosition: position } } });
  };
  const onChangeNavTextColor = ({ rgb: { r, g, b, a } }) =>
    updatePageData({ variables: { cache: { navTextColor: `rgba(${r},${g},${b},${a})` } } });
  const headerPositionConfig = {
    label: 'Header',
    color:
      headerPosition === 'inner'
        ? { onChange: onChangeNavTextColor, color: navTextColor || defaultNavTextColor, tooltip: 'Set banner navigation text color' }
        : undefined,
    position: showBanner
      ? {
          dataTip: eq(headerPosition, 'outer') ? 'In Banner' : 'Above Banner',
          icon: eq(headerPosition, 'outer') ? 'down' : 'up',
          onClick: () => updateHeaderPosition(eq(headerPosition, 'outer') ? 'inner' : 'outer'),
        }
      : undefined,
  };
  const updateBannerBackgroundColor = ({ rgb: { r, g, b }, hex }) => {
    updatePageData({ variables: { cache: { banner: { backgroundColor: hex || `rgb(${r},${g},${b})` } } } });
  };
  const toggleBanner = () =>
    updatePageData({ variables: { cache: { banner: { enable: !showBanner }, ...(showBanner ? { headerPosition: 'outer' } : {}) } } });
  const onChangeBannerTextDecorationColor = ({ rgb: { r, g, b, a } }) =>
    updatePageData({ variables: { cache: { banner: { textDecorationColor: `rgba(${r},${g},${b},${a})` } } } });

  const onChangeBannerTextDecoration = ({ target: { value } }) =>
    updatePageData({ variables: { cache: { banner: { textDecoration: value } } } });
  const onChangeBannerImageOpacity = ({ rgb: { a } }) => updatePageData({ variables: { cache: { banner: { opacity: a } } } });
  const onChangeBannerImageBlendMode = ({ target: { value } }) =>
    updatePageData({ variables: { cache: { banner: { imageBlendMode: value } } } });
  const onChangeBannerWithImage = () => updatePageData({ variables: { cache: { banner: { withImage: !withImage } } } });

  const bannerConfig = {
    label: 'Banner',
    color: { onChange: updateBannerBackgroundColor, color: bannerBackgroundColor || 'rgb(255,255,255)', disableAlpha: true },
    toggle: { checked: showBanner || false, onChange: toggleBanner },
  };
  const bannerTextConfig = {
    label: 'Banner Text',
    color: { onChange: onChangeBannerTextDecorationColor, color: textDecorationColor || 'rgb(0,0,0,0.3)' },
    select: {
      className: 'number-of-columns',
      onChange: onChangeBannerTextDecoration,
      value: textDecoration,
      options: [
        { value: '', label: 'No Decoration' },
        { value: 'stripe', label: 'Stripe' },
        { value: 'border', label: 'Border' },
      ],
    },
    text: { dataTip: 'Edit', onClick: () => setState({ variables: { modalTypeShown: 'bannerText' } }) },
  };
  const bannerImageConfig = { label: 'Banner Image', toggle: { checked: withImage || false, onChange: onChangeBannerWithImage } };
  const bannerImageStyleConfig = {
    label: '',
    color: { onChange: onChangeBannerImageOpacity, color: `rgba(0,0,0,${opacity ?? 1})`, alphaOnly: true },
    select: {
      className: 'number-of-columns',
      onChange: onChangeBannerImageBlendMode,
      value: imageBlendMode,
      options: backgroundBlendModes,
    },
    image: { dataTip: 'Upload', onClick: () => setState({ variables: { modalTypeShown: 'bannerImage' } }) },
  };
  const updateFeedBackgroundColor = ({ rgb: { r, g, b, a } }) =>
    updatePageData({ variables: { cache: { newsFeed: { backgroundColor: `rgba(${r},${g},${b},${a})` } } } });
  const updateFeedPosition = (position) => updatePageData({ variables: { cache: { newsFeed: { position } } } });
  const handleShowNewsUpdate = () => updatePageData({ variables: { cache: { newsFeed: { enabled: !showNews } } } });
  const newsFeedConfig = {
    label: 'News Feed',
    color: { onChange: updateFeedBackgroundColor, color: feedBackgroundColor || 'rgba(255,255,255,0)' },
    position: showBanner
      ? {
          dataTip: eq(newsFeedPosition, 'top') ? 'Bottom' : 'Top',
          onClick: () => updateFeedPosition(eq(newsFeedPosition, 'top') ? 'bottom' : 'top'),
          icon: eq(newsFeedPosition, 'top') ? 'down' : 'up',
        }
      : undefined,
    list: { dataTip: 'edit', onClick: () => setState({ variables: { modalTypeShown: 'newsFeed' } }) },
    toggle: { checked: showNews || false, onChange: handleShowNewsUpdate },
  };
  // const updateIntroBackgroundColor = ({ rgb: { r, g, b, a } }) =>
  //   updatePageData({
  //     variables: {
  //       cache: { introContent: { backgroundColor: `rgba(${r},${g},${b},${a})` } },
  //     },
  //   });
  // const introSectionConfig = {
  //   label: 'Intro Section',
  //   color: {
  //     onChange: updateIntroBackgroundColor,
  //     color: introBackgroundColor || 'rgba(255,255,255,0)',
  //   },
  //   text: {
  //     onClick: () => setState({ variables: { modalTypeShown: 'IntroText' } }),
  //     dataTip: 'Edit',
  //   },
  // };
  const updateMainContentBackgroundColor = ({ rgb: { r, g, b, a } }) =>
    updatePageData({ variables: { cache: { mainContentBackgroundColor: `rgba(${r},${g},${b},${a})` } } });
  const handleSelectNumberOfColumns = ({ target: { value } }) => {
    if (eq(overlapCardTitle, 'bottom')) {
      updatePageData({ variables: { cache: { columns: parseInt(value, 10), overlapCardTitle: 'overlap' } } });
    } else {
      updatePageData({ variables: { cache: { columns: parseInt(value, 10) } } });
    }
  };
  const mainContentConfig = {
    label: 'Main Content',
    color: { onChange: updateMainContentBackgroundColor, color: mainContentBackgroundColor || 'rgba(255,255,255,0)' },
    select: {
      className: 'number-of-columns',
      onChange: handleSelectNumberOfColumns,
      value: numberOfColumns,
      options: [
        { value: 1, label: '1 columns' },
        { value: 2, label: '2 columns' },
        { value: 3, label: '3 columns' },
        { value: 4, label: '4 columns' },
        { value: 5, label: '5 columns' },
      ],
    },
  };
  const handleShowProgress = () => updatePageData({ variables: { cache: { showProgress: !showProgress } } });
  const progressConfig = {
    label: 'Display Progress on Homepage',
    toggle: { checked: showProgress || false, onChange: handleShowProgress },
  };
  const switchCardTitlePosition = ({ target: { value } }) => updatePageData({ variables: { cache: { overlapCardTitle: value } } });
  const cardTitlePositionConfig = {
    label: 'Card Title Position',
    select: {
      className: 'number-of-columns',
      onChange: switchCardTitlePosition,
      value: overlapCardTitle,
      options: [
        { value: 'overlap', label: 'Overlap' },
        ...(numberOfColumns > 1 ? [{ value: 'bottom', label: 'Bottom' }] : []),
        { value: 'none', label: 'None' },
      ],
    },
  };
  const toggleCardDescription = () => updatePageData({ variables: { cache: { showCardDescription: !showCardDescription } } });
  const displayCardDescriptionConfig = {
    label: 'Display Card Description',
    toggle: { checked: showCardDescription || false, onChange: toggleCardDescription },
  };
  const addSubText = () =>
    updatePageData({
      variables: { cache: { subContent: concat(subTexts, [{ text: 'New sub content', backgroundColor: 'rgba(255,255,255,0)' }]) } },
    });
  const addIntroText = () =>
    updatePageData({
      variables: {
        cache: { introContent: concat(introTexts, [{ text: 'New intro content', type: 'text', backgroundColor: 'rgba(255,255,255,0)' }]) },
      },
    });
  const addIntroStatus = () =>
    updatePageData({
      variables: { cache: { introContent: concat(introTexts, [{ type: 'status', text: '', backgroundColor: 'rgba(255,255,255,0)' }]) } },
    });
  return (
    <div className="homepage-settings">
      {eq(headerPosition, 'outer') && <Section config={headerPositionConfig} />}
      <Section config={bannerConfig} customClass="homepage-layout middle-thumbnail-content">
        {headerPosition === 'inner' && <Section config={headerPositionConfig} />}
        {showBanner && newsFeedPosition === 'top' && <Section config={newsFeedConfig} customClass="news-feed-section" />}
        <Section config={bannerTextConfig} customClass="intro-section" />
        <Section config={bannerImageConfig} customClass="intro-section" />
        {!!withImage && <Section config={bannerImageStyleConfig} customClass="intro-section" />}
        {showBanner && newsFeedPosition === 'bottom' && <Section config={newsFeedConfig} customClass="news-feed-section" />}
      </Section>
      {eq(showBanner, false) && <Section config={newsFeedConfig} customClass="news-feed-section" />}
      <Section config={{ label: 'Page Body' }} customClass="homepage-layout middle-thumbnail-content">
        <Section config={{ label: 'Intro Section' }} customClass="intro-section">
          {introTexts.map(({ type: introType }, index) => (
            <Section
              config={{
                label: introType === 'status' ? 'Overall Status/Progress' : 'Intro Content',
                text:
                  introType === 'status'
                    ? undefined
                    : {
                        dataTip: 'Edit',
                        onClick: () => setState({ variables: { selectedIntroContentIndex: index, modalTypeShown: 'introText' } }),
                      },
                remove: {
                  dataTip: 'Delete',
                  onClick: () => updatePageData({ variables: { cache: { introContent: introTexts.filter((_, id) => id !== index) } } }),
                },
                color:
                  introType === 'status'
                    ? undefined
                    : {
                        onChange: ({ rgb: { r, g, b, a } }) => {
                          const introContent = set(
                            JSON.parse(JSON.stringify(introTexts)),
                            [index, 'backgroundColor'],
                            `rgba(${r},${g},${b},${a})`,
                          );

                          updatePageData({ variables: { cache: { introContent } } });
                        },
                        color: introTexts[index].backgroundColor || 'rgba(255,255,255,0)',
                      },
                index,
              }}
              customClass="homepage-layout middle-thumbnail-content intro-content"
              key={join(['intro', 'text', index], '-')}
            />
          ))}
          <Section config={{ label: 'Add Section' }} customClass="add-section">
            <button onClick={addIntroText} type="button" className="add-section-btn">
              <FontAwesomeIcon icon={['fal', 'plus']} />
              Add Intro Section
            </button>
            {filter(introTexts, ['type', 'status']).length ? (
              ''
            ) : (
              <button onClick={addIntroStatus} type="button" className="add-section-btn">
                <FontAwesomeIcon icon={['fal', 'plus']} />
                Add Overall Status/Progress
              </button>
            )}
          </Section>
        </Section>
        <Section config={mainContentConfig} customClass="homepage-layout middle-thumbnail-content" />
        {progressable && <Section config={progressConfig} customClass="progress-layout" />}
        <Section config={cardTitlePositionConfig} customClass="progress-layout" />
        <Section config={displayCardDescriptionConfig} customClass="progress-layout card-description" />
        {rows}
        <Section config={{ label: 'Sub Section' }} customClass="sub-section">
          {subTexts.map((st, index) => (
            <Section
              config={{
                label: 'Sub Content',
                text: {
                  dataTip: 'Edit',
                  onClick: () => setState({ variables: { a: 'b', selectedSubContentIndex: index, modalTypeShown: 'subText' } }),
                },
                remove: {
                  dataTip: 'Delete',
                  onClick: () => updatePageData({ variables: { cache: { subContent: subTexts.filter((_, id) => id !== index) } } }),
                },
                color: {
                  onChange: ({ rgb: { r, g, b, a } }) => {
                    const subContent = set(JSON.parse(JSON.stringify(subTexts)), [index, 'backgroundColor'], `rgba(${r},${g},${b},${a})`);

                    updatePageData({ variables: { cache: { subContent } } });
                  },
                  color: subTexts[index].backgroundColor || 'rgba(255,255,255,0)',
                },
                index,
              }}
              customClass="homepage-layout middle-thumbnail-content sub-content"
              key={join(['sub', 'text', index], '-')}
            />
          ))}
          <Section config={{ label: 'Add Section' }} customClass="add-section">
            <button onClick={addSubText} type="button" className="add-section-btn">
              <FontAwesomeIcon icon={['fal', 'plus']} />
              Add Sub Section
            </button>
          </Section>
        </Section>
      </Section>
    </div>
  );
}
